import { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import queryString from "query-string";
import { toast } from "react-toastify";

import api from "libs/api";
import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

const DashboardContext = createContext();

const DashboardProvider = ({ children }) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const filters = queryString.parse(search);

  const {
    isLoading: loading,
    data: dashboard,
    refetch: refetchTasks,
  } = useQuery({
    queryKey: ["webmaster_dashboard", filters],
    queryFn: () =>
      api.get(
        `/webmaster/dashboard?${queryString.stringify({
          ...filters,
          type: "upcoming_tasks",
          sort: filters?.sort || "due_date",
        })}`,
      ),
  });

  const filterHandler = (name, value, type) => {
    navigate(
      `?${queryString.stringify({
        ...filters,
        [name]: value || undefined,
        type: !!value ? type : undefined,
      })}`,
    );
  };

  const updateTask = (id, values) =>
    new Promise(async (resolve, reject) => {
      const taskQueries = queryClient.getQueriesData({
        queryKey: ["webmaster_dashboard"],
        predicate: (query) => query.queryKey[0] === "webmaster_dashboard",
      });

      const previousQueryStates = new Map();

      try {
        taskQueries.forEach(([queryKey, queryData]) => {
          if (!queryData?.tasks_upcoming_date) return;

          previousQueryStates.set(queryKey, { ...queryData });

          queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData?.tasks_upcoming_date) return oldData;

            const queryFilters = queryKey[1] || {};

            return {
              ...oldData,
              tasks_upcoming_date: oldData.tasks_upcoming_date
                .map((task) => {
                  if (task._id === id) {
                    const updatedTask = { ...task, ...values };

                    if (
                      updatedTask.status &&
                      ![2, 3, 4].includes(updatedTask.status)
                    ) {
                      return null;
                    }

                    if (queryFilters.status) {
                      const statusValue = task_status_data?.find(
                        (item) => item?.key === queryFilters.status,
                      )?.value;

                      if (statusValue && updatedTask.status !== statusValue) {
                        return null;
                      }
                    }

                    if (queryFilters.type) {
                      const typeValue = task_priority_data?.find(
                        (item) => item?.key === queryFilters.type,
                      )?.value;

                      if (typeValue && updatedTask.priority !== typeValue) {
                        return null;
                      }
                    }

                    return updatedTask;
                  }

                  return task;
                })
                .filter(Boolean),
            };
          });
        });

        await api.put(`/webmaster/tasks/${id}`, values);

        await queryClient.invalidateQueries({
          queryKey: ["webmaster_dashboard"],
        });

        resolve();
      } catch (err) {
        taskQueries.forEach(([queryKey]) => {
          const originalData = previousQueryStates.get(queryKey);
          if (originalData) {
            queryClient.setQueryData(queryKey, originalData);
          }
        });

        console.error(err);
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const value = useMemo(() => {
    return {
      data: dashboard,
      loading,
      filters,
      filterHandler,
      updateTask,
      refetchTasks,
    };
    // eslint-disable-next-line
  }, [dashboard, filters, loading]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

const useDashboard = () => useContext(DashboardContext);
export { DashboardContext, useDashboard };
export default DashboardProvider;
